/**
 * The Schema landing page — phase 2 §7.3.
 *
 * It exists to say what this surface is, because the screens inside it look like
 * an admin console and are not one: they are the manual counterpart to the
 * resolution test suite (phase 0 §4.8), and acceptance criteria depend on them.
 * Somebody who arrives here from a document's "who can reach this" link should
 * be able to tell in one sentence why the shell changed around them.
 */

import { Link } from '@tanstack/react-router';
import { useSpaces } from '../../api/queries.ts';
import { Card, PageHeader } from '../../components/ui/layout.tsx';

function Section({ title, to, children }: { title: string; to: string; children: string }) {
  return (
    <Link to={to} className="no-underline">
      <Card className="h-full transition-colors hover:bg-secondary">
        <h2 className="text-md font-semibold text-primary">{title}</h2>
        <p className="mt-2 text-sm text-tertiary">{children}</p>
      </Card>
    </Link>
  );
}

export function SchemaIndexPage() {
  const spaces = useSpaces();

  return (
    <>
      <PageHeader
        title="Schema"
        description="Introspection over the substrate — the tree as rows, and who resolves to what. These are not scaffolding: §4.8 makes the effective-access views the manual counterpart to the resolution suite, and several acceptance criteria are read off them. They are simply not navigation for people doing their jobs."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Section title="Spaces & nodes" to="/schema/spaces">
          Every space you can reach, as a tree with its depth counters, and the grants sitting on
          each one. Creating a standalone space lives here too — §4.2 makes that admin work, since
          spaces are otherwise a byproduct of an organization or a project.
        </Section>
        <Section title="Effective access" to="/schema/access">
          The two §4.8 views: everyone who resolves on one node, and everywhere one person resolves.
          Both answer through the same SQL function that enforces access, which is what makes them
          an explanation rather than a second opinion.
        </Section>
      </div>

      <Card>
        <h2 className="text-md font-semibold text-primary">What is not here yet</h2>
        <p className="mt-2 text-sm text-secondary">
          The event log. §6.3 names the audit view as one of the log's four consumers and nothing
          renders it yet, so it is absent rather than stubbed — a nav item leading to an empty page
          would claim a surface exists.
        </p>
        <p className="mt-3 text-sm text-tertiary">
          You can currently reach {spaces.data ? spaces.data.spaces.length : '—'} spaces.{' '}
          <Link to="/" className="link">
            Back to Matchbox OS
          </Link>
          .
        </p>
      </Card>
    </>
  );
}
